import React from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { MoipValidator } from 'moip-sdk-js';

import { addMinutes, differenceInMinutes } from 'date-fns';
import { AxiosResponse } from 'axios';

import { userApi } from 'src/api';
import pagarme from 'src/api/pagarme';
import { PaymentMethodPayload } from 'src/types/api';
import {
  PaymentType,
  // User,
  PaymentMethod,
  PaymentMethodStatus,
  SellStatus,
  SellPayInfo,
} from 'src/types/dto';
import { useToast } from 'src/hooks';
import { useMeQuery } from 'src/hooks/queries/user';
import { useCheckoutWalletByIdMutation } from 'src/hooks/queries/checkout';
import {
  useCheckoutWalletMutation,
  useCheckoutWalletValidateMutation,
} from 'src/hooks/mutations/checkout';

import { useEventContext } from '../provider';
import { useEventHistory, useReferral } from '../hooks';
import {
  InvalidFieldName,
  useCartStore,
  useCheckoutFormValuesStore,
  useCheckoutStore,
} from '../stores';
import { map } from 'lodash';
import { putSellPay } from '../api';
import useModalFeedback from './useModalFeedback';
import { useGetCheckoutRoutesVerification } from './useGetCheckoutRoutesVerification';
import { usePaymentForm } from '../Checkout/CheckoutPayment/usePaymentForm';
import { useAddCardForm } from '../Checkout/CheckoutAddCard/useAddCardForm';

type PostTokenRequestBody = {
  card: {
    number: string;
    holder_name: string;
    exp_month: number;
    exp_year: number;
    cvv: string;
    // label: string;
  };
  type: 'card';
};

const timeout = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

const formatDateSafariSafe = (value: string) => {
  const values = value.split('/');
  return `${values[2].trim()}-${values[1].trim().padStart(2, '0')}-${values[0]
    .trim()
    .padStart(2, '0')}`;
};

export const getSellStatus = (sellId: number, ref: string) =>
  userApi
    .get(`sell/${sellId}/status`, { params: { ref } })
    .then(res => res.data);

const postPagarmeToken = (payload: PostTokenRequestBody, appId: string) =>
  pagarme
    .post<PostTokenRequestBody, AxiosResponse<{ id: string }>>(
      'tokens',
      payload,
      { params: { appId } },
      // {
      //   headers: {
      //     Authorization: `Basic ${Buffer.from(publicKey).toString('base64')}`,
      //   },
      // },
    )
    .then(res => res.data);

// uncomment to enable easy changes in feedback component and debug some behavior
// let statusCounter = 0;
// export const getSellStatus = sellId =>
//   new Promise(resolve =>
//     setTimeout(() => {
//       console.log(`mocking 'sell/${sellId}/status' call...`);
//       resolve(
//         statusCounter === 2 ? 'paymentCancelled' : 'paymentWaitingConfirmation',
//       );
//       statusCounter++;
//     }, 2000),
//   );
// export const putSellPay = (sellId, payload) =>
//   new Promise(resolve =>
//     setTimeout(() => {
//       console.log(`mocking 'sell/${sellId}/pay' call...`);
//       resolve('mock response');
//     }, 200),
//   );

const useRecursiveTimeout = <T>(
  callback: () => Promise<T>,
  delay: number | null,
) => {
  const savedCallback = React.useRef<() => Promise<T>>();

  // Remember the latest callback.
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  React.useEffect(() => {
    let id: NodeJS.Timeout;

    const tick = () => {
      const ret = savedCallback.current?.();

      if (ret instanceof Promise) {
        ret
          .then(() => {
            if (delay !== null) {
              id = setTimeout(tick, delay);
            }
          })
          .catch(() => {
            if (id) {
              clearTimeout(id);
            }
          });
      } else {
        if (delay !== null) {
          id = setTimeout(tick, delay);
        }
      }
    };

    if (delay !== null) {
      id = setTimeout(tick, delay);
      return () => {
        if (id) {
          clearTimeout(id);
        }
      };
    }
  }, [delay]);
};

const useConfirmCardForm = () => {
  const { setInvalidFieldsNames, setInvalidFieldsMessage } = useEventContext();
  const { formValues } = useCheckoutFormValuesStore();

  const trigger = () => {
    const invalidFieldsNames: InvalidFieldName[] = [];
    const invalidFieldsMessage = [];

    if ((formValues.amountCharged?.trim()?.length ?? 0) === 0) {
      invalidFieldsNames.push('amountCharged');
      invalidFieldsMessage.push(
        'É preciso informar um valor para verificarmos o cartão',
      );
    }

    setInvalidFieldsNames?.(invalidFieldsNames);
    setInvalidFieldsMessage?.(invalidFieldsMessage);
    return invalidFieldsNames.length === 0;
  };

  return { trigger, values: formValues };
};

const useNextEventButton = () => {
  const eventHistory = useEventHistory();
  const location = useLocation();
  const { eventID } = useParams();
  const meQuery = useMeQuery();
  const walletMutation = useCheckoutWalletMutation();
  const walletValidateMutation = useCheckoutWalletValidateMutation();
  const {
    getEventCart,
    // reinitializeEventCart,
  } = useCartStore();
  const { getEventCheckout } = useCheckoutStore();
  const { formValues, setFormValues, clearSensitiveFormValues } =
    useCheckoutFormValuesStore();
  const addCardForm = useAddCardForm();
  const confirmCardForm = useConfirmCardForm();
  const paymentForm = usePaymentForm();
  const { isAddCardRoute, isConfirmCardRoute, isTicketsRoute, isPaymentRoute } =
    useGetCheckoutRoutesVerification();
  const modalFeedback = useModalFeedback();
  const [isSellCreditCardPaySuccess, setSellCreditCardPaySuccess] =
    React.useState(false);
  const [isLoading, setLoading] = React.useState(false);
  const isCreditCardPollingDisabledRef = React.useRef(false);
  const isCreditCardPollingTriesRef = React.useRef(0);
  const referral = useReferral(eventID!);
  const toast = useToast();
  const checkoutWalletByIdMutation = useCheckoutWalletByIdMutation({
    retry: attemptIndex => attemptIndex < 20,
    retryDelay: 2 * 1000,
  });

  const resetPollingState = () => {
    isCreditCardPollingTriesRef.current = 0;
    isCreditCardPollingDisabledRef.current = true;
    setSellCreditCardPaySuccess(false);
    setLoading(false);
    clearSensitiveFormValues();
  };

  useRecursiveTimeout(
    async () => {
      if (isCreditCardPollingTriesRef.current === 20) {
        resetPollingState();
        eventHistory.push('error');
        throw new Error('stop polling');
      }

      if (
        isCreditCardPollingTriesRef.current < 20 &&
        !isCreditCardPollingDisabledRef.current
      ) {
        const sellStatus = await getSellStatus(
          getEventCheckout(eventID!).sellId,
          referral,
        );
        if (
          sellStatus === 'paymentWaitingConfirmation' &&
          isCreditCardPollingTriesRef.current === 20 - 1
        ) {
          resetPollingState();
          eventHistory.push('error');
          throw new Error('stop polling');
        } else if (sellStatus === 'paymentCancelled') {
          resetPollingState();
          eventHistory.push('rejected');
          throw new Error('stop polling');
        } else if (sellStatus === 'paymentApproved') {
          resetPollingState();
          eventHistory.push('success');
          throw new Error('stop polling');
        }
      }

      isCreditCardPollingTriesRef.current =
        isCreditCardPollingTriesRef.current + 1;
    },
    isSellCreditCardPaySuccess && !isCreditCardPollingDisabledRef.current
      ? 2000
      : null,
  );

  const onNext = async () => {
    if (isTicketsRoute) {
      const hasTicketsOnCart = getEventCart(eventID!)?.some(
        item => item.quantity > 0,
      );

      if (hasTicketsOnCart) {
        if (meQuery.data?.email) {
          eventHistory.push(`/event/${eventID}/checkout/payment`);
        } else {
          eventHistory.push(`/event/${eventID}/checkout/login`);
        }
      } else {
        toast({
          description: 'Adicione pelo menos um ingresso para prosseguir',
          status: 'warning',
          duration: 6000,
        });
      }

      return;
    }

    if (isAddCardRoute) {
      const isFormValid = addCardForm.trigger();
      if (isFormValid) {
        const noSpacesCardNumber = addCardForm.values.cardNumber.replace(
          /\s/g,
          '',
        );
        const { brand } = MoipValidator.cardType(noSpacesCardNumber);
        if (!brand) {
          toast({
            description:
              'O número de cartão informado ainda não é suportado pelo processador de pagamento',
            status: 'warning',
          });
          return;
        }

        const checkout = getEventCheckout(eventID!);

        const payload: PaymentMethodPayload = {
          paymentType: PaymentType.CreditCard,
          cardToken: addCardForm.values.cardToken,
          brand,
          name: noSpacesCardNumber.slice(-4),
          holderName: addCardForm.values.payerName,
          holderBirthdate: formatDateSafariSafe(
            addCardForm.values.payerBirthdate,
          ),
          holderDocument: addCardForm.values.payerCpf,
          holderPhone: addCardForm.values.payerPhone,
          holderZipCode: addCardForm.values.payerZipCode,
          holderState: addCardForm.values.payerState,
          holderCity: addCardForm.values.payerCity,
          holderDistrict: addCardForm.values.payerDistrict,
          holderAddress: addCardForm.values.payerAddress,
          holderStreetNumber:
            (addCardForm.values.payerStreetNumber &&
              parseInt(addCardForm.values.payerStreetNumber)) ||
            0,
          holderComplement: addCardForm.values.payerComplement,
        };

        if (checkout.mediator === 'pagarme') {
          const number = addCardForm.values.cardNumber.replace(/\s/g, '');
          const splitCardExpiration =
            addCardForm.values.cardExpiration.split(' / ');
          const exp_month = parseInt(
            splitCardExpiration[0]?.replace(/^0(?!\d)/, '')?.trim(),
          );
          const exp_year = parseInt(splitCardExpiration[1]?.trim());
          const token = await postPagarmeToken(
            {
              card: {
                number,
                holder_name: addCardForm.values.payerName,
                exp_month,
                exp_year,
                cvv: addCardForm.values.cardCvc,
              },
              type: 'card',
            },
            checkout.sellerKey,
          );
          payload.cardToken = token.id;
        }

        let paymentMethod: PaymentMethod;
        setLoading(true);

        try {
          paymentMethod = await walletMutation.mutateAsync(payload);

          if (paymentMethod.status === PaymentMethodStatus.Valid) {
            await modalFeedback.openAsync({
              title: 'Cartão já adicionado',
              message:
                'Esse cartão já foi adicionado e verificado e encontra-se disponível para uso.',
            });
            setFormValues({
              selectedPaymentType: PaymentType.CreditCard,
              selectedCardId: paymentMethod.id,
              selectedCardName: paymentMethod.name,
              selectedCardBrand: paymentMethod.brand,
            });
            clearSensitiveFormValues();
            setLoading(false);
            eventHistory.push('payment');
            return;
          } else if (paymentMethod.status === PaymentMethodStatus.Invalid) {
            await modalFeedback.openAsync({
              title: 'Cartão bloqueado por 48h',
              message:
                'Seu cartão atingiu o limite de tentativas de verificação. Pague agora usando PIX ou outro cartão. Este cartão será liberado para novas tentativas em 48h.',
            });
            clearSensitiveFormValues();
            setLoading(false);
            eventHistory.push('payment-methods');
            return;
          } else if (paymentMethod.status === PaymentMethodStatus.Rejected) {
            await modalFeedback.openAsync({
              title: 'Cartão recusado',
              message:
                'Seu pagamento não foi aprovado, confira seu limite, os dados do seu cartão e tente novamente.',
            });
            clearSensitiveFormValues();
            setLoading(false);
            eventHistory.push('payment-methods');
            return;
          } else if (paymentMethod.status === PaymentMethodStatus.Failed) {
            await modalFeedback.openAsync({
              title: 'Falha ao tentar adicionar cartão',
              message:
                'Houve um problema desconhecido, o cartão não pôde ser adicionado. Tente novamente mais tarde ou pague por PIX.',
            });
            clearSensitiveFormValues();
            setLoading(false);
            eventHistory.push('payment-methods');
            return;
          }
        } catch (er) {
          clearSensitiveFormValues();
          setLoading(false);
          toast({
            description: 'Ocorreu um erro ao cadastrar o seu novo cartão',
            status: 'warning',
          });
          return;
        }

        try {
          await timeout(2 * 1000);
          paymentMethod = await checkoutWalletByIdMutation.mutateAsync(
            paymentMethod.id,
          );

          setLoading(false);

          if (paymentMethod.status === PaymentMethodStatus.Valid) {
            await modalFeedback.openAsync({
              title: 'Cartão já adicionado',
              message:
                'Esse cartão já foi adicionado e verificado e encontra-se disponível para uso.',
            });
            setFormValues({
              selectedPaymentType: PaymentType.CreditCard,
              selectedCardId: paymentMethod.id,
              selectedCardName: paymentMethod.name,
              selectedCardBrand: paymentMethod.brand,
            });
            eventHistory.push('payment');
            return;
          } else if (paymentMethod.status === PaymentMethodStatus.Invalid) {
            await modalFeedback.openAsync({
              title: 'Cartão bloqueado por 48h',
              message:
                'Seu cartão atingiu o limite de tentativas de verificação. Pague agora usando PIX ou outro cartão. Este cartão será liberado para novas tentativas em 48h.',
            });
            eventHistory.push('payment-methods');
            return;
          } else if (paymentMethod.status === PaymentMethodStatus.Rejected) {
            await modalFeedback.openAsync({
              title: 'Cartão recusado',
              message:
                'A adição do seu cartão não foi realizada pois seu cartão não aprovou a cobrança provisória de verificação, confira seu limite, se os dados do seu cartão estão corretas e tente novamente. Você também pode pagar por Pix se preferir.',
            });
            eventHistory.push('payment-methods');
            return;
          } else if (paymentMethod.status === PaymentMethodStatus.Failed) {
            await modalFeedback.openAsync({
              title: 'Falha ao tentar adicionar cartão',
              message:
                'Houve um problema desconhecido, o cartão não pôde ser adicionado. Tente novamente mais tarde ou pague por PIX.',
            });
            eventHistory.push('payment-methods');
            return;
          }

          eventHistory.push(`confirm-card/${paymentMethod.id}`);
        } catch (er) {
          setLoading(false);
          await modalFeedback.openAsync({
            title: 'Falha ao tentar adicionar cartão',
            message:
              'Houve um problema desconhecido, o cartão não pôde ser adicionado. Tente novamente mais tarde ou pague por PIX.',
          });
          eventHistory.push('payment-methods');
        } finally {
          clearSensitiveFormValues();
        }
      }
    }

    if (isConfirmCardRoute) {
      const isFormValid = confirmCardForm.trigger();
      if (isFormValid) {
        try {
          setLoading(true);
          const locationSplitted = location.pathname.split('/');
          const response = await walletValidateMutation.mutateAsync({
            id: locationSplitted[locationSplitted.length - 1],
            amountCharged: Math.round(
              parseFloat(
                confirmCardForm.values.amountCharged
                  .replace('R$', '')
                  .replace(',', '.'),
              ) * 100,
            ),
          });

          setLoading(false);

          if (
            (response.status === PaymentMethodStatus.Pending &&
              response.validationDetail?.lastError) ||
            response.status === PaymentMethodStatus.Invalid
          ) {
            const remainingAttempts =
              response.validationDetail?.remainingAttempts ?? 0;
            await modalFeedback.openAsync({
              title:
                remainingAttempts === 0
                  ? 'Cartão bloqueado por 48h'
                  : `Valor incorreto. ${remainingAttempts === 1 ? `Última tentativa`: `Restam ${remainingAttempts} tentativas`}.`,
              message:
                remainingAttempts === 0
                  ? 'Seu cartão atingiu o limite de tentativas de verificação. Pague agora usando PIX ou outro cartão. Este cartão será liberado para novas tentativas em 48h.'
                  : `O valor informado é diferente do cobrado no cartão. ${remainingAttempts === 1 ? `Última tentativa`: `Restam ${remainingAttempts} tentativas`} até bloqueio de 48h.`,
            });
            if (remainingAttempts === 0) {
              eventHistory.push(`event/${eventID}/checkout/payment-methods`);
            }
            // toast({
            //   description:
            //     response.validationDetail?.lastError === 'timeout'
            //       ? 'Você demorou muito para informar o valor'
            //       : 'O valor informado está incorreto',
            //   status: 'warning',
            // });
            return;
          }

          setFormValues({
            selectedPaymentType: PaymentType.CreditCard,
            selectedCardId: response.id,
            selectedCardName: response.name,
            selectedCardBrand: response.brand,
          });
          eventHistory.push(`payment`);
        } catch (er) {
          toast({
            description: 'Ocorreu um erro ao validar o seu novo cartão',
            status: 'warning',
          });
          setLoading(false);
        }
      }
    }

    if (isPaymentRoute) {
      const isFormValid = paymentForm.trigger();
      if (isFormValid) {
        if (
          !formValues.selectedPaymentType ||
          (formValues.selectedPaymentType === PaymentType.CreditCard &&
            !formValues.selectedCardId)
        ) {
          eventHistory.push('payment-methods');
        } else if (formValues.selectedPaymentType === PaymentType.Pix) {
          try {
            setLoading(true);
            const response = await putSellPay({
              sellId: getEventCheckout(eventID!).sellId,
              payload: {
                paymentType: PaymentType.Pix,
                payerCpf: formValues.payerCpf,
                payerPhone: formValues.payerPhone,
                owners: map(formValues.owners, ticket => ({
                  ticketTypeId: parseInt(ticket.ticketTypeId),
                  name: ticket.name,
                  buyerInfoData: ticket.buyerInfoData,
                })),
              },
              params: { ref: referral },
            });

            if (response.status === SellStatus.Cancelled)
              throw new Error('Venda cancelada');

            const now = Date.now();
            const expirationDifferenceInMinutes = differenceInMinutes(
              new Date(response.expiresAt || ''),
              new Date(response.createdAt || ''),
            );
            setFormValues({
              amount: response.amount,
              qrCode: response.qrCode,
              qrCodeUrl: response.qrCodeUrl,
              expirationDifferenceInMinutes,
              expiresAtInMilliseconds: addMinutes(
                now,
                expirationDifferenceInMinutes,
              ).getTime(),
              createdAtInMilliseconds: now,
            });

            eventHistory.push('pix');
          } catch (er) {
            eventHistory.push('error');
          }
        } else if (formValues.selectedCardId) {
          try {
            setLoading(true);
            const payload: SellPayInfo = {
              paymentType: PaymentType.CreditCard,
              paymentMethodId: formValues.selectedCardId,
              cvv: formValues.selectedCardCvv.trim(),
              owners: map(formValues.owners, ticket => ({
                ticketTypeId: parseInt(ticket.ticketTypeId),
                name: ticket.name,
                buyerInfoData: ticket.buyerInfoData,
              })),
              installments: formValues.installments,
            };
            await putSellPay({
              sellId: getEventCheckout(eventID!).sellId,
              payload,
              params: { ref: referral },
            });
            isCreditCardPollingDisabledRef.current = false;
            setSellCreditCardPaySuccess(true);
          } catch (er) {
            resetPollingState();
            eventHistory.push('error');
          }
        } else if (formValues.selectedPaymentType === PaymentType.Gratuito) {
          try {
            setLoading(true);
            const response = await putSellPay({
              sellId: getEventCheckout(eventID!).sellId,
              payload: {
                paymentType: PaymentType.Gratuito,
                payerCpf: formValues.payerCpf,
                payerPhone: formValues.payerPhone,
                owners: map(formValues.owners, ticket => ({
                  ticketTypeId: parseInt(ticket.ticketTypeId),
                  name: ticket.name,
                  buyerInfoData: ticket.buyerInfoData,
                })),
              },
              params: { ref: referral },
            });

            if (response.status === SellStatus.Cancelled)
              throw new Error('Venda cancelada');

            eventHistory.push('success');
          } catch (er) {
            eventHistory.push('error');
          }
        }
      }
    }
  };

  return [onNext, isLoading];
};

export default useNextEventButton;
